/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {SimpleGrid, Stack} from '@chakra-ui/react'
import useCustomer from '../../commerce-api/hooks/useCustomer'
import Checkbox from '../checkbox/index.jsx'
import useAddressFields from './useAddressFields'
import Field from '../field'
import useAutocompleteAddress from './useAutocompleteAddress'
import {FormattedMessage, useIntl} from 'react-intl'
import {createProductLineItems} from '../../tracking/analytics'
import useBasket from '../../commerce-api/hooks/useBasket'
import useEffectEvent from '../../hooks/use-effect-event'

const testHouseNr = (v) => /\d+/.test(v)

const AddressFields = ({form, prefix = ''}) => {
    const customer = useCustomer()
    const basket = useBasket()
    const productItems = basket.productItems?.map((product) =>
        createProductLineItems(product, basket._productItemsDetail)
    )
    const fields = useAddressFields({form, prefix})
    const {ref} = useAutocompleteAddress(form, prefix)
    const {locale} = useIntl()

    // begin: additional address
    const [additionalAddressVisible, setAdditionalAddressVisible] = useState(
        !!form?.getValues(fields.address2.name)
    )
    const onAdditionalAddressVisibilityChange = useEffectEvent((visible) => {
        if (!visible) {
            // clear additional address fields when user closes them
            form.setValue(fields.address2.name, '')
        }
    })
    useEffect(() => {
        onAdditionalAddressVisibilityChange(additionalAddressVisible)
    }, [additionalAddressVisible, onAdditionalAddressVisibilityChange])
    const additionalAddress = (
        <Checkbox
            size="sm"
            isChecked={additionalAddressVisible}
            onChange={(e) => {
                setAdditionalAddressVisible(e.target.checked)
            }}
            data-testid="openAddress2-checkbox-testid"
        >
            <FormattedMessage
                defaultMessage="Additional address information (optional)"
                id="use_address_fields.label.address2_openAddress2"
            />
        </Checkbox>
    )
    // end: additional address

    // begin: address house number
    const [confirmedNoHouseNr, setConfirmedNoHouseNr] = useState(true)
    const address1Value = form.watch('address1')
    const address1Dirty = form.formState.dirtyFields.address1
    const address1HasHouseNr = testHouseNr(address1Value)
    const showHouseNrConfirmation = address1Dirty && !address1HasHouseNr
    // changing address1 clears confirmation, but for pristine value it is assumed to have been confirmed
    useEffect(() => {
        setConfirmedNoHouseNr(!address1Dirty)
    }, [address1Dirty, address1Value])
    // revalidate address field on confirmation change
    const onHouseNrConfirmationChange = useEffectEvent(() => {
        form.trigger('address1')
    })
    useEffect(() => {
        if (address1Dirty) {
            onHouseNrConfirmationChange(confirmedNoHouseNr)
        }
    }, [address1Dirty, confirmedNoHouseNr, onHouseNrConfirmationChange])
    const confirmNoHouseNr = (
        <Checkbox
            size="sm"
            isChecked={confirmedNoHouseNr}
            onChange={() => setConfirmedNoHouseNr((val) => !val)}
            isInvalid={!confirmedNoHouseNr}
        >
            <FormattedMessage
                id="use_address_fields.label.no_house_nr"
                defaultMessage="I confirm, that my shipping address doesn't contain a house number."
            />
        </Checkbox>
    )
    const address1 = (
        <Field
            {...fields.address1}
            rules={{
                ...fields.address1.rules,
                validate: {
                    hasHouseNr: (value) => confirmedNoHouseNr || testHouseNr(value)
                }
            }}
            ref={ref}
            analyticsEvent="personal_information"
            productItems={productItems}
        />
    )
    // end: address house number

    let addressFields
    switch (locale) {
        case 'ja-JP':
            addressFields = (
                <Stack spacing={6}>
                    <SimpleGrid columns={{base: 1, md: 2}} spacingX={2} spacingY={6}>
                        <Field
                            {...fields.lastName}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                        <Field
                            {...fields.firstName}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                    </SimpleGrid>
                    <SimpleGrid columns={{base: 1, md: 2}} spacingX={2} spacingY={6}>
                        <Field
                            {...fields.countryCode}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                    </SimpleGrid>
                    <SimpleGrid columns={{base: 2, md: 3}} spacingX={2} spacingY={6}>
                        <Field
                            {...fields.postalCode}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                        <Field
                            {...fields.stateCode}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                        <Field
                            {...fields.city}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                    </SimpleGrid>
                    {address1}
                    {showHouseNrConfirmation && confirmNoHouseNr}
                    <Stack spacing={2.5}>
                        {additionalAddress}
                        {additionalAddressVisible && (
                            <Field
                                {...fields.address2}
                                analyticsEvent="personal_information"
                                productItems={productItems}
                            />
                        )}
                    </Stack>
                    <SimpleGrid columns={{base: 1, md: 2}} spacingX={2} spacingY={6}>
                        <Field
                            {...fields.phone}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                    </SimpleGrid>
                    {customer.isRegistered && <Field {...fields.preferred} />}
                </Stack>
            )
            break
        case 'en-US':
            addressFields = (
                <Stack spacing={6}>
                    <SimpleGrid columns={{base: 1, md: 2}} spacingX={2} spacingY={6}>
                        <Field
                            {...fields.firstName}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                        <Field
                            {...fields.lastName}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                    </SimpleGrid>
                    {address1}
                    {showHouseNrConfirmation && confirmNoHouseNr}
                    <Stack spacing={2.5}>
                        {additionalAddress}
                        {additionalAddressVisible && (
                            <Field
                                {...fields.address2}
                                analyticsEvent="personal_information"
                                productItems={productItems}
                            />
                        )}
                    </Stack>
                    <SimpleGrid columns={{base: 2, md: 3}} spacingX={2} spacingY={6}>
                        <Field
                            {...fields.postalCode}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                        <Field
                            {...fields.city}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                        <Field
                            {...fields.stateCode}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                        <Field
                            {...fields.countryCode}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                    </SimpleGrid>
                    <SimpleGrid columns={{base: 1, md: 2}} spacingX={2} spacingY={6}>
                        <Field
                            {...fields.phone}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                    </SimpleGrid>
                    {customer.isRegistered && <Field {...fields.preferred} />}
                </Stack>
            )
            break
        case 'ko-KR':
            addressFields = (
                <Stack spacing={6}>
                    <SimpleGrid columns={{base: 1, md: 2}} spacingX={2} spacingY={6}>
                        <Field
                            {...fields.firstName}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                        <Field
                            {...fields.lastName}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                    </SimpleGrid>
                    <Field
                        {...fields.phone}
                        analyticsEvent="personal_information"
                        productItems={productItems}
                    />
                    <Field
                        {...fields.postalCode}
                        analyticsEvent="personal_information"
                        productItems={productItems}
                    />
                    <Field
                        {...fields.address1}
                        ref={ref}
                        analyticsEvent="personal_information"
                        productItems={productItems}
                    />
                    <Stack spacing={2.5}>
                        <Checkbox
                            size="sm"
                            isChecked={additionalAddressVisible}
                            onChange={(e) => {
                                setAdditionalAddressVisible(e.target.checked)
                            }}
                            data-testid="openAddress2-checkbox-testid"
                        >
                            <FormattedMessage
                                defaultMessage="Additional address information (optional)"
                                id="use_address_fields.label.address2_openAddress2"
                            />
                        </Checkbox>
                        {additionalAddressVisible && (
                            <Field
                                {...fields.address2}
                                analyticsEvent="personal_information"
                                productItems={productItems}
                            />
                        )}
                    </Stack>
                    <Field
                        {...fields.city}
                        analyticsEvent="personal_information"
                        productItems={productItems}
                    />
                    <Field
                        {...fields.countryCode}
                        analyticsEvent="personal_information"
                        productItems={productItems}
                    />
                    {customer.isRegistered && <Field {...fields.preferred} />}
                </Stack>
            )
            break
        default:
            addressFields = (
                <Stack spacing={6}>
                    <Field
                        {...fields.salutation}
                        analyticsEvent="salutation"
                        productItems={productItems}
                    />
                    <SimpleGrid columns={{base: 1, md: 2}} spacingX={2} spacingY={6}>
                        <Field
                            {...fields.firstName}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                        <Field
                            {...fields.lastName}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                    </SimpleGrid>
                    {address1}
                    {showHouseNrConfirmation && confirmNoHouseNr}
                    <Stack spacing={2.5}>
                        {additionalAddress}
                        {additionalAddressVisible && (
                            <Field
                                {...fields.address2}
                                analyticsEvent="personal_information"
                                productItems={productItems}
                            />
                        )}
                    </Stack>
                    <SimpleGrid columns={{base: 2, md: 3}} spacingX={2} spacingY={6}>
                        <Field
                            {...fields.postalCode}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                        <Field
                            {...fields.city}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                        <Field
                            {...fields.countryCode}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                    </SimpleGrid>
                    <SimpleGrid columns={{base: 1, md: 2}} spacingX={2} spacingY={6}>
                        <Field
                            {...fields.phone}
                            analyticsEvent="personal_information"
                            productItems={productItems}
                        />
                    </SimpleGrid>
                    {customer.isRegistered && <Field {...fields.preferred} />}
                </Stack>
            )
    }

    return addressFields
}

AddressFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,
    selectedAddress: PropTypes.any
}

export default AddressFields
